import styled from 'styled-components';
import { ThemeInterface } from '../../interfaces/themeInterface';

const monoStack =
  "'SFMono-Regular', Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace";

export const Wrapper = styled.div`
  position: relative;
`;

export const Btn = styled.button`
  background: ${({ theme }: { theme: ThemeInterface }) =>
    theme.name === 'short'
      ? 'linear-gradient(180deg, rgba(255, 255, 255, 0.13) 0%, rgba(255, 255, 255, 0.05) 100%)'
      : 'linear-gradient(180deg, rgba(0, 0, 0, 0.12) 0%, rgba(0, 0, 0, 0.04) 100%)'};
  border: 1px solid
    ${({ theme }: { theme: ThemeInterface }) =>
      theme.name === 'short' ? 'rgba(255, 255, 255, 0.32)' : 'rgba(0, 0, 0, 0.2)'};
  border-radius: 999px;
  color: ${({ theme }: { theme: ThemeInterface }) => theme.text};
  font-size: 0.62rem;
  font-family: ${monoStack};
  letter-spacing: 0.08em;
  cursor: pointer;
  padding: 0.35rem 0.7rem;
  opacity: 0.9;
  line-height: 1;
  box-shadow: inset 0 1px 0
    ${({ theme }: { theme: ThemeInterface }) =>
      theme.name === 'short' ? 'rgba(255, 255, 255, 0.14)' : 'rgba(0, 0, 0, 0.08)'};
  transition:
    transform 0.16s ease,
    opacity 0.2s ease,
    border-color 0.2s ease,
    background 0.2s ease;

  &:hover {
    opacity: 1;
    transform: translateY(-1px);
    border-color: ${({ theme }: { theme: ThemeInterface }) =>
      theme.name === 'short' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.28)'};
    background: ${({ theme }: { theme: ThemeInterface }) =>
      theme.name === 'short'
        ? 'linear-gradient(180deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.08) 100%)'
        : 'linear-gradient(180deg, rgba(0, 0, 0, 0.16) 0%, rgba(0, 0, 0, 0.06) 100%)'};
  }

  &:focus-visible {
    outline: none;
    box-shadow:
      0 0 0 2px ${({ theme }: { theme: ThemeInterface }) =>
        theme.name === 'short' ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.18)'},
      inset 0 1px 0
        ${({ theme }: { theme: ThemeInterface }) =>
          theme.name === 'short' ? 'rgba(255, 255, 255, 0.14)' : 'rgba(0, 0, 0, 0.08)'};
  }
`;

export const Popover = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 10px;
  background: ${({ theme }: { theme: ThemeInterface }) =>
    theme.name === 'short'
      ? 'linear-gradient(180deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.05) 100%), rgba(0, 0, 255, 0.96)'
      : 'linear-gradient(180deg, rgba(0, 0, 0, 0.12) 0%, rgba(0, 0, 0, 0.03) 100%), rgba(255, 204, 213, 0.96)'};
  border: 1px solid
    ${({ theme }: { theme: ThemeInterface }) =>
      theme.name === 'short' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.2)'};
  border-radius: 14px;
  padding: 20px 24px;
  width: clamp(320px, 34vw, 390px);
  max-height: min(72vh, 760px);
  overflow-y: auto;
  z-index: 2400;
  font-family: ${monoStack};
  box-shadow:
    inset 0 1px 0
      ${({ theme }: { theme: ThemeInterface }) =>
        theme.name === 'short' ? 'rgba(255, 255, 255, 0.16)' : 'rgba(0, 0, 0, 0.1)'},
    0 18px 38px ${({ theme }: { theme: ThemeInterface }) =>
      theme.name === 'short' ? 'rgba(0, 0, 0, 0.28)' : 'rgba(0, 0, 0, 0.12)'};
  backdrop-filter: blur(7px);
`;

export const Title = styled.h3`
  font-size: 0.72rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  margin: 0 0 0.75rem 0;
  color: ${({ theme }: { theme: ThemeInterface }) => theme.text};
  opacity: 0.92;
`;

export const Paragraph = styled.p`
  font-size: 0.68rem;
  line-height: 1.58;
  color: ${({ theme }: { theme: ThemeInterface }) => theme.text};
  opacity: 0.8;
  margin: 0 0 0.75rem 0;
`;

export const StepLine = styled.p`
  font-size: 0.68rem;
  line-height: 1.45;
  color: ${({ theme }: { theme: ThemeInterface }) => theme.text};
  opacity: 0.8;
  margin: 0;
`;

export const Divider = styled.hr`
  border: none;
  border-top: 1px solid
    ${({ theme }: { theme: ThemeInterface }) =>
      theme.name === 'short' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.14)'};
  margin: 1.05rem 0;
  opacity: 1;
`;

export const SectionTitle = styled.h4`
  font-size: 0.67rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin: 0 0 0.5rem 0;
  color: ${({ theme }: { theme: ThemeInterface }) => theme.text};
  opacity: 0.9;
`;

export const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 0.75rem 0;
`;

export const ListItem = styled.li`
  font-size: 0.67rem;
  line-height: 1.7;
  color: ${({ theme }: { theme: ThemeInterface }) => theme.text};
  opacity: 0.78;
  padding-left: 1rem;
  position: relative;

  &::before {
    content: '\u2022';
    position: absolute;
    left: 0;
    opacity: 0.8;
  }
`;

export const Emphasis = styled.p`
  font-size: 0.67rem;
  line-height: 1.8;
  color: ${({ theme }: { theme: ThemeInterface }) => theme.text};
  opacity: 0.72;
  margin: 0;
  font-style: italic;
`;
